import { useEffect, useState } from 'react'
import { useEntityTags, useAssignTag, useUnassignTag, useCreateTag } from '../../api/tags'
import type { Tag } from '../../api/tags'
import { TagBadgeLink } from './TagBadgeLink'
import { TagPicker } from './TagPicker'
import { badgeVariants } from '../ui/badge'
import { cn } from '../../lib/utils'

/** Tailwind's own breakpoints, in px — the two this component can fold at. */
const COLLAPSE_BREAKPOINTS = { md: 768, lg: 1024 } as const

export interface EntityTagsCollapse {
    below: keyof typeof COLLAPSE_BREAKPOINTS
    max: number
}

interface EntityTagsSectionProps {
    org: string
    entityType: 'asset' | 'document' | 'user' | 'contract' | 'issue' | 'expense' | 'conversation'
    entityUid: string
    className?: string
    /**
     * Fold the list below a breakpoint (spec 2026-09-17-06). An entity header
     * is one wrapping row: at 375 px three tags push the action buttons a full
     * line down, which is exactly what the AFUL page did. Below `below` only
     * the first `max` tags render, followed by a `+N` chip that expands the
     * rest IN PLACE on tap; above it, every tag as before.
     *
     * A media query cannot do this on its own — hiding the overflow in CSS
     * still leaves the `+N` chip lying about the count, and the expanded state
     * has to survive a resize.
     */
    collapse?: EntityTagsCollapse
    /**
     * Whether the picker trigger (the tag-plus icon) and each chip's remove
     * cross render at all. Defaults to `true`: tags are always editable in
     * place, with no pencil and no Done step. A page passes its write
     * permission here, so a reader gets the badges only.
     */
    editable?: boolean
}

/** `true` while the viewport is narrower than `breakpoint`. */
function useBelow(breakpoint: keyof typeof COLLAPSE_BREAKPOINTS | undefined) {
    const px = breakpoint ? COLLAPSE_BREAKPOINTS[breakpoint] : 0
    // Lazy initializer rather than `false`, for the same reason `useIsMobile`
    // has one: this is a client-only SPA, and correcting one tick later makes
    // a phone render the wide branch first.
    const [below, setBelow] = useState<boolean>(() => (px ? window.innerWidth < px : false))
    useEffect(() => {
        if (!px) {
            setBelow(false)
            return
        }
        const mql = window.matchMedia(`(max-width: ${px - 1}px)`)
        const onChange = () => setBelow(window.innerWidth < px)
        mql.addEventListener('change', onChange)
        onChange()
        return () => mql.removeEventListener('change', onChange)
    }, [px])
    return below
}

export function EntityTagsSection({ org, entityType, entityUid, className, collapse, editable = true }: EntityTagsSectionProps) {
    const { data: entityTags = [] } = useEntityTags(org, entityType, entityUid)
    const [expanded, setExpanded] = useState(false)
    const folded = useBelow(collapse?.below)
    const assignMutation = useAssignTag(org)
    const unassignMutation = useUnassignTag(org)
    const createTagMutation = useCreateTag(org)

    const handleTagAdd = (tag: Tag) => {
        assignMutation.mutate({
            entity_type: entityType,
            entity_uid: entityUid,
            tag_uid: tag.uid!,
        })
    }

    const handleTagRemove = (tag: Tag) => {
        unassignMutation.mutate({
            entity_type: entityType,
            entity_uid: entityUid,
            tag_uid: tag.uid!,
        })
    }

    const handleTagCreate = (name: string) => {
        createTagMutation.mutate(
            { name, target_entity_types: [entityType] },
            {
                onSuccess: (newTag) => {
                    if (newTag?.uid) {
                        assignMutation.mutate({
                            entity_type: entityType,
                            entity_uid: entityUid,
                            tag_uid: newTag.uid,
                        })
                    }
                },
            }
        )
    }

    const max = collapse?.max ?? 0
    const foldedNow = folded && !expanded && max > 0 && entityTags.length > max
    const shownTags = foldedNow ? entityTags.slice(0, max) : entityTags
    const hiddenCount = entityTags.length - shownTags.length

    return (
        <div data-testid="entity-tags-section" className={className || "flex flex-wrap items-center gap-2 px-6 py-2"}>
            {shownTags.map((tag) => (
                <TagBadgeLink
                    key={tag.uid}
                    org={org}
                    tag={{ uid: tag.uid!, name: tag.name || '', code: tag.code, color: tag.color, icon: tag.icon }}
                    onRemove={editable ? () => handleTagRemove(tag) : undefined}
                />
            ))}
            {hiddenCount > 0 && (
                <button
                    type="button"
                    className={cn(
                        badgeVariants({ variant: 'outline' }),
                        'cursor-pointer whitespace-nowrap hover:bg-muted'
                    )}
                    onClick={() => setExpanded(true)}
                    aria-label={`+${hiddenCount}`}
                    title={entityTags.slice(max).map((tag) => tag.name).join(', ')}
                    data-testid="entity-tags-more"
                >
                    +{hiddenCount}
                </button>
            )}
            {editable && (
                <TagPicker
                    orgCode={org}
                    entityType={entityType}
                    selectedTags={entityTags}
                    onTagAdd={handleTagAdd}
                    onTagRemove={handleTagRemove}
                    onTagCreate={handleTagCreate}
                />
            )}
        </div>
    )
}
